// StorageStock.tsx

import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { FaArrowDown, FaArrowUp, FaUser, FaBox, FaClock, FaHashtag, FaExchangeAlt, FaMapMarkerAlt } from 'react-icons/fa';
import MiniLoadingSpinner from '../../../../../../../components/Spinners/MiniLoadingSpinner';
import { getAllMovementsByStorageUnitIdAndDateRange } from '../../../../../../../crud/management_api/storage_unit_movements';
import StorageUnitsDateFilter from './StorageUnitsDateFilter/StorageUnitsDateFilter';
import './StorageUnits.scss'

interface StorageStockProps {
  show: boolean;
  onClose: () => void;
  storageID: string; // Se espera el storage unit id como string
}

interface Movement {
  movement_id: number;
  is_purchase: boolean;
  is_consumption: boolean;
  is_manual: boolean;
  is_system: boolean;
  storage_unit_id_from: number | null;
  storage_unit_id_to: number | null;
  storage_unit_id_from_name: string | null;
  storage_unit_id_to_name: string | null;
  username: string | null;
  item_name: string | null;
  utility_name: string | null;
  ingredient_name: string;
  local_timestamp: string; // ISO string
  old_quantity: number;
  new_quantity: number;
  acronym: string;
}

const StorageStock: React.FC<StorageStockProps> = ({ show, onClose, storageID }) => {
  const [movements, setMovements] = useState<Movement[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Estados para el filtro de fechas
  const [startDate, setStartDate] = useState<string>(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  // Reiniciamos los estados cuando se cierra el modal
  useEffect(() => {
    if (!show) {
      setMovements([]);
      setError(null);
    }
  }, [show]);

  useEffect(() => {
    if (!show) return;

    const fetchMovements = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!startDate || !endDate) {
          throw new Error('Por favor, selecciona un rango de fechas válido.');
        }
        const response = await getAllMovementsByStorageUnitIdAndDateRange(
          storageID,
          startDate,
          endDate
        );
        if (!response.data || !Array.isArray(response.data)) {
          throw new Error('Respuesta de la API inválida');
        }
        setMovements(response.data);
      } catch (err: any) {
        console.error('Error al obtener los movimientos:', err);
        setError(err.message || 'Error desconocido al obtener los movimientos.');
      } finally {
        setLoading(false);
      }
    };

    fetchMovements();
  }, [show, storageID, startDate, endDate]);

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // El useEffect se encargará de actualizar los movimientos al cambiar startDate y endDate
  };

  const getMovementType = (movement: Movement): JSX.Element => {
    let badgeClass = 'movement-badge';
    let icon = null;
    let text = '';

    if (movement.is_purchase) {
      badgeClass += ' purchase';
      text = 'Compra';
    } else if (movement.is_consumption) {
      badgeClass += ' consumption';
      text = 'Consumo';
    } else if (movement.is_manual) {
      badgeClass += ' manual';
      text = 'Manual';
    } else if (movement.is_system) {
      badgeClass += ' system';
      text = 'Sistema';
    } else {
      badgeClass += ' unknown';
      text = 'Sin tipo';
    }

    return <span className={badgeClass}>{text}</span>;
  };

  const getQuantityDifferenceDisplay = (movement: Movement) => {
    const diff = movement.new_quantity - movement.old_quantity;
    if (diff > 0) {
      return (
        <span style={{ color: 'green' }}>
          <FaArrowUp /> {diff}
        </span>
      );
    } else if (diff < 0) {
      return (
        <span style={{ color: 'red' }}>
          <FaArrowDown /> {Math.abs(diff)}
        </span>
      );
    } else {
      return <span style={{ color: 'gray' }}>0</span>;
    }
  };

  // Función para obtener el nombre del producto de acuerdo a los 3 atributos
  const getProductName = (movement: Movement): string => {
    if (movement.item_name) return movement.item_name;
    if (movement.utility_name) return movement.utility_name;
    return movement.ingredient_name;
  };

  return (    <Modal show={show} onHide={onClose} size="lg" centered className="storage-units-modal">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Movimientos del Almacén</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={onClose}>
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body className="storage-modal-body">        {/* Formulario de filtro de fechas */}
        <Form onSubmit={handleFilterSubmit} className="mb-3">
          <Row>
            <StorageUnitsDateFilter
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />
          </Row>
        </Form>

        {loading ? (
          <div className="text-center">
            <MiniLoadingSpinner />
          </div>
        ) : movements.length === 0 ? (
          <div className="text-center">¡No existen movimientos en el periodo seleccionado!</div>
        ): error ? (
          <div className="alert alert-danger text-center" role="alert">
            {error}
          </div>
        ) : (          <div className="table-responsive">
            <table className="custom-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Producto</th>
                  <th>Fecha</th>
                  <th className="d-none d-md-table-cell">Cantidad antigua</th>
                  <th>Cantidad actual</th>
                  <th>Tipo</th>
                  <th className="d-none d-sm-table-cell">Origen</th>
                  <th className="d-none d-sm-table-cell">Destino</th>
                  <th>Diferencia</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((movement) => (
                  <tr key={movement.movement_id}>
                    <td>{movement.username}</td>
                    <td>{getProductName(movement)}</td>
                    <td>{new Date(movement.local_timestamp).toLocaleString()}</td>
                    <td className="d-none d-md-table-cell">
                      {movement.old_quantity} {movement.acronym}
                    </td>
                    <td>
                      {movement.new_quantity} {movement.acronym}
                    </td>
                    <td>{getMovementType(movement)}</td>
                    <td className="d-none d-sm-table-cell">
                      <span className="location-badge origin">
                        {movement.storage_unit_id_from_name ?? 'Cliente'}
                      </span>
                    </td>
                    <td className="d-none d-sm-table-cell">
                      <span className="location-badge destination">
                        {movement.storage_unit_id_to_name ?? 'Cliente'}
                      </span>
                    </td>
                    <td>{getQuantityDifferenceDisplay(movement)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom w-100 d-flex justify-content-end">
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default StorageStock;
